import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { currentUser as queryCurrentUser, getCurrentUserMenu } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { getApiBaseUrl } from '@/utils/apiConfig';
import { message } from 'antd';
import { createAppLayout } from './modules/app-core';
import type { AppInitialState } from './modules/app-core';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = ['/user/login', '/user/register', '/user/login/'];

// 辅助函数：检查是否应该获取用户信息
function shouldFetchUserInfo(pathname: string): boolean {
  return !loginPath.includes(pathname);
}

// 辅助函数：检查是否需要显示Google 2FA
function checkGoogle2FARequired(currentUser?: API.CurrentUser): boolean {
  return !!(currentUser && !currentUser.google2faEnabled);
}

// 辅助函数：获取用户信息
async function fetchUserInfo(): Promise<API.CurrentUser | undefined> {
  try {
    const currentUser = await queryCurrentUser();
    return currentUser;
  } catch (error) {
    console.error('Failed to fetch user info:', error);
    return undefined;
  }
}

// 辅助函数：获取用户菜单
async function fetchUserMenu(): Promise<API.MenuRoute[] | undefined> {
  try {
    const menuResult = await getCurrentUserMenu();
    return menuResult?.data?.availableMenus;
  } catch (error) {
    console.error('Failed to fetch user menu:', error);
    return undefined;
  }
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<AppInitialState> {
  // 如果不是登录页面，执行
  const { location } = history;
  console.log('location', location, shouldFetchUserInfo(location.pathname));
  if (shouldFetchUserInfo(location.pathname)) {
    try {
      const currentUser = await fetchUserInfo();

      // 获取用户菜单权限
      let currentUserMenu: API.MenuRoute[] | undefined;
      if (currentUser) {
        currentUserMenu = await fetchUserMenu();
      }

      // 检查是否需要显示谷歌验证码绑定弹窗
      const showGoogle2FAModal = checkGoogle2FARequired(currentUser);

      return {
        fetchUserInfo,
        fetchUserMenu,
        currentUser,
        currentUserMenu,
        settings: defaultSettings as Partial<LayoutSettings>,
        showGoogle2FAModal,
        menuLastUpdated: Date.now()
      };
    } catch (error) {
      console.error('getInitialState error:', error);
      // 即使出错也要返回基本结构
      return {
        fetchUserInfo,
        fetchUserMenu,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
  }

  return {
    fetchUserInfo,
    fetchUserMenu,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return createAppLayout({
    initialState: initialState as AppInitialState,
    setInitialState: setInitialState as (state: AppInitialState | ((prev: AppInitialState) => AppInitialState)) => void
  });
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: getApiBaseUrl(),
  ...errorConfig,
  timeout: 10000,
};
